import type { Leave } from '@trello/action-history';
import { featureFlagClient } from '@trello/feature-flag-client';
import { client } from '@trello/graphql';
import { forNamespace } from '@trello/i18n';

import type { Card as LegacyCardModel } from 'app/scripts/models/Card';
import type { Trace } from '../ActionMapTypes';
import type { CardActionFragment } from '../CardActionFragment.generated';
import { CardActionFragmentDoc } from '../CardActionFragment.generated';
import { CardActionDocument } from '../CardActionMutation.generated';
import { NoopError } from '../NoopError';
import { onTracedCardUpdate } from '../onTracedCardUpdate';
import { recordCardAction } from '../recordCardAction';

const format = forNamespace('notificationsGrouped', {
  shouldEscapeStrings: false,
});

export const leave = (card: LegacyCardModel, action: Leave, trace: Trace) => {
  const useGraphQlActions = featureFlagClient.get(
    'xf.undo-redo-rewrite',
    false,
  );

  const cardId = card.id;
  const memberId = action.idMember;
  const { traceId } = trace;

  if (useGraphQlActions) {
    const cardFragment = client.readFragment<CardActionFragment>({
      id: `Card:${cardId}`,
      fragment: CardActionFragmentDoc,
    });

    if (!cardFragment?.idMembers.includes(memberId)) {
      throw new NoopError();
    }

    const newMembers = cardFragment.idMembers.filter(
      (id: string) => id !== memberId,
    );

    recordCardAction(cardId, action);

    client.mutate({
      mutation: CardActionDocument,
      variables: {
        cardId,
        card: {
          idMembers: newMembers,
        },
        traceId,
      },
      optimisticResponse: {
        __typename: 'Mutation',
        updateCard: {
          __typename: 'Card',
          id: card.id,
          subscribed: false,
          idMembers: newMembers,
        },
      },
    });
  } else {
    if (!card.hasMember(action.idMember)) {
      throw new NoopError();
    }
    card.removeMemberWithTracing(
      action.idMember,
      traceId,
      onTracedCardUpdate({ card, ...trace }),
    );
  }

  return format('notification_left_card');
};
